import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { Breadcrumbs, useTheme } from "@mui/material";
import { MouseEvent } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation } from "react-router-dom";
import { setCurrentNavigationPath } from "redux-store/features/GlobalState";
import type { RootState } from "redux-store/store";

/**
 * Props for the GenerateBreadcrumbs component
 */
interface GenerateBreadcrumbsProps {
  /** Test ID for the breadcrumbs container */
  id?: string;
}

/**
 * Individual breadcrumb item data
 */
interface BreadcrumbData {
  /** Display label for the breadcrumb */
  label: string;
  /** Optional URL for navigation */
  url?: string;
  /** Optional click handler */
  onClick?: () => void;
  /** Blanks the label in Sentry session replays. Set on crumbs holding patient data. */
  masked?: boolean;
}

/**
 * Breadcrumb pattern configuration for route matching
 */
interface BreadcrumbPattern {
  /** Regex pattern to match against the current URL */
  pattern: RegExp;
  /** Optional URL for the pattern */
  url?: string;
  /** Array of breadcrumb items to display */
  crumbsData: BreadcrumbData[];
}

/**
 * Location state for navigation source tracking
 */
interface LocationState {
  from?: string;
}

const GenerateBreadcrumbs = ({ id }: GenerateBreadcrumbsProps) => {
  // Get patient data for header
  const { getSpecificPatientData } = useSelector((state: RootState) => state.specificPatient);
  const theme = useTheme();
  const dispatch = useDispatch();
  const { currentNavigationPath } = useSelector((state: RootState) => state.globalStates);
  const location = useLocation();

  // Check if navigation came from open encounters using location.state
  const navigationSource = (location.state as LocationState | null)?.from;

  const breadcrumbsData: BreadcrumbPattern[] = [
    {
      pattern: /^\/$/,
      url: "/",
      crumbsData: [{ label: "Nephrolytics" }],
    },
    {
      pattern: /^\/appointments$/,
      url: "/appointments",
      crumbsData: [{ label: "Nephrolytics", url: "/" }, { label: "Scheduled Patients" }],
    },
    {
      pattern: /^\/patients$/,
      url: "/patients",
      crumbsData: [{ label: "Nephrolytics", url: "/" }, { label: "All Patients" }],
    },
    {
      pattern: /^\/encounters$/,
      url: "/encounters",
      crumbsData: [{ label: "Nephrolytics", url: "/" }, { label: "Open Encounters" }],
    },
    {
      pattern: /^\/settings\?tab=profileSettings$/,
      url: "/settings?tab=profileSettings",
      crumbsData: [{ label: "Nephrolytics", url: "/" }, { label: "Profile Settings" }],
    },
    {
      pattern: /^\/settings\?tab=myTeam$/,
      url: "/settings?tab=myTeam",
      crumbsData: [{ label: "Nephrolytics", url: "/" }, { label: "My Team" }],
    },
    {
      pattern: /^\/settings\?tab=privacySecurity$/,
      url: "/settings?tab=privacySecurity",
      crumbsData: [{ label: "Nephrolytics", url: "/" }, { label: "Privacy & Security" }],
    },
    {
      pattern: /^\/settings\?tab=integrations$/,
      url: "/settings?tab=integrations",
      crumbsData: [{ label: "Nephrolytics", url: "/" }, { label: "EMR Credentials" }],
    },
    {
      pattern: /^\/schedule\/patient\/[^/]+\/appointment\/[^/]+$/,
      crumbsData: [
        { label: "Nephrolytics", url: "/" },
        { label: "Scheduled Patients", url: "/appointments" },
        {
          label: `${getSpecificPatientData?.first_name ?? ""} ${getSpecificPatientData?.last_name ?? ""}`,
          masked: true,
        },
      ],
    },
    {
      pattern: /^\/patients\/[^/]+$/,
      crumbsData: [
        { label: "Nephrolytics", url: "/" },
        // Show "Open Encounters" if navigation came from encounters page, otherwise "All Patients"
        {
          label: navigationSource === "encounters" ? "Open Encounters" : "All Patients",
          url: navigationSource === "encounters" ? "/encounters" : "/patients",
        },
        {
          label: `${getSpecificPatientData?.first_name ?? ""} ${getSpecificPatientData?.last_name ?? ""}`,
          masked: true,
          onClick: () => {
            dispatch(setCurrentNavigationPath(null));
          },
        },
        ...(currentNavigationPath ? [{ label: `${currentNavigationPath}` }] : []),
      ],
    },
  ];

  const findClosestMatch = (url: string): BreadcrumbPattern => {
    const closestMatch = breadcrumbsData.find((item) => item.pattern.test(url));
    // If no match is found, return the first item in the breadcrumbsData array
    return closestMatch || breadcrumbsData[0];
  };

  const breadCrumbs = findClosestMatch(window.location.pathname + window.location.search);

  return (
    <Breadcrumbs
      data-testid={id}
      aria-label="breadcrumb"
      separator={
        <NavigateNextIcon
          fontSize="small"
          sx={{
            color: theme?.palette?.common.blue,
            marginLeft: "4px !important",
            marginRight: "4px !important",
          }}
        />
      }
      sx={{
        ".MuiBreadcrumbs-separator": {
          margin: "0 !important",
        },
      }}>
      {breadCrumbs?.crumbsData?.map((crumb, index) => {
        const isLastItem = index === breadCrumbs?.crumbsData.length - 1;
        const hasValidUrl = crumb?.url && crumb.url !== "#";
        const isClickable = !isLastItem && hasValidUrl;
        // Presence of the attribute is what the replay mask selector matches on.
        const maskAttr = crumb.masked ? { "data-sentry-mask": "" } : {};

        return isClickable ? (
          <Link
            key={index}
            color="inherit"
            style={{
              textDecoration: "none",
              fontSize: "0.875rem",
              fontWeight: "400",
              color: theme?.palette?.common?.blue,
            }}
            {...(typeof crumb?.onClick === "function"
              ? {
                  onClick: (e: MouseEvent<HTMLAnchorElement>) => {
                    e.preventDefault();
                    crumb.onClick?.();
                  },
                }
              : {})}
            to={index === 0 ? "/" : (crumb?.url ?? "#")}>
            <span
              {...maskAttr}
              style={{
                borderBottom: "1px solid transparent",
                fontWeight: "500",
                marginBottom: "12px",
              }}
              onMouseEnter={(e) => ((e.target as HTMLSpanElement).style.borderBottom = "1px solid #000")}
              onMouseLeave={(e) => ((e.target as HTMLSpanElement).style.borderBottom = "1px solid transparent")}>
              {crumb.label}
            </span>
          </Link>
        ) : (
          <span
            key={index}
            {...maskAttr}
            style={{
              fontSize: "0.875rem",
              fontWeight: "500",
              color: theme?.palette?.themePrimary?.primary,
              marginBottom: "12px",
              cursor: "default",
            }}>
            {crumb.label}
          </span>
        );
      })}
    </Breadcrumbs>
  );
};

export default GenerateBreadcrumbs;
